import { useState, useEffect, type CSSProperties } from "react";
import { useNavigate, useParams } from "react-router-dom";
import PinsBackground from "@/components/PinsBackground";
import { useClient } from "@/contexts/ClientContext";

import RewardsMarquee from "@/components/RewardsMarquee";
import TeasingIntro from "@/components/TeasingIntro";
import { DigitalMenuViewer } from "@/components/DigitalMenuViewer";
import { PlayCtaFloating } from "@/components/PlayCtaFloating";
import { PlayerLegalFooter } from "@/components/PlayerLegalFooter";
import { usePlayerIdentity } from "@/hooks/usePlayerIdentity";
import { BrandTile } from "@/components/BrandTile";
import { GameTile, type GameKey } from "@/components/GameTile";

/** Stagger index for the entrance sequence (see .pt-rise in index.css). */
const rise = (i: number) => ({ "--i": i } as CSSProperties);

const Index = () => {
  const navigate = useNavigate();
  const { clientSlug } = useParams<{clientSlug: string;}>();
  const { client, clientSettings } = useClient();
  const { blockedActions, isLoading: identityLoading } = usePlayerIdentity();

  const slug = clientSlug || client?.slug || 'default';
  const [showTeasing, setShowTeasing] = useState(true);

  const currentSlug = clientSlug || "default";
  const baseRoute = `/c/${currentSlug}`;

  const HAS_PLAYED_KEY = `feednback_has_played_${slug}`;

  // Sync server-side blocked actions to localStorage flag
  useEffect(() => {
    if (!identityLoading && blockedActions.length > 0) {
      localStorage.setItem(HAS_PLAYED_KEY, 'true');
    }
  }, [identityLoading, blockedActions, HAS_PLAYED_KEY]);

  const hasPlayed = localStorage.getItem(HAS_PLAYED_KEY) === 'true';

  const wheelEnabled = clientSettings?.games_enabled?.wheel !== false;
  const isNewPlayer = !hasPlayed && blockedActions.length === 0;

  // Redirect only truly new visitors to wheel after teasing
  useEffect(() => {
    if (!showTeasing && !identityLoading && isNewPlayer && wheelEnabled) {
      navigate(`${baseRoute}/play/wheel`, { replace: true });
    }
  }, [showTeasing, identityLoading, isNewPlayer, wheelEnabled]);

  // Show teasing overlay for first-time visitors (no flash)
  if (showTeasing) {
    return <TeasingIntro onDone={() => setShowTeasing(false)} />;
  }

  // While loading, if the player might be redirected, show nothing to avoid flash
  if ((identityLoading || !clientSettings) && !hasPlayed) {
    return null;
  }

  // If new player + wheel enabled, redirect is pending via useEffect — show nothing
  if (isNewPlayer && wheelEnabled) {
    return null;
  }

  
  const hasMenu = !!client?.menu_config;
  const wheelOn = clientSettings?.games_enabled?.wheel !== false;
  const blackjackOn = clientSettings?.games_enabled?.blackjack !== false;
  const slotOn = clientSettings?.games_enabled?.slot !== false;

  const games: { key: GameKey; label: string; route: string }[] = [];
  if (wheelOn) games.push({ key: "wheel", label: "Roue de la chance", route: `${baseRoute}/play/wheel` });
  if (blackjackOn) games.push({ key: "blackjack", label: "Blackjack express", route: `${baseRoute}/play/blackjack` });
  if (slotOn) games.push({ key: "slot", label: "Machine à sous", route: `${baseRoute}/play/slot` });

  // ===== Menu-first layout: when a digital menu is configured, it takes the page =====
  if (hasMenu) {
    return (
      <div className="relative flex min-h-[100dvh] flex-col items-center px-4 pt-4 pb-36">
        <PinsBackground />
        <div className="relative z-10 w-full max-w-2xl space-y-4">
          {/* Compact header: the venue first */}
          <div className="flex items-center gap-3 pt-1">
            <BrandTile size="md" animate decorative />
            <div className="min-w-0">
              <h2 className="truncate text-lg font-bold text-foreground sm:text-xl">
                {client?.name || " "}
              </h2>
              <p className="truncate text-xs text-muted-foreground">
                Découvre la carte, puis joue pour gagner
              </p>
            </div>
          </div>

          {/* Menu inline */}
          <DigitalMenuViewer menuConfig={client.menu_config as any} inline />

        </div>

        {/* Floating CTA */}
        <PlayCtaFloating
          baseRoute={baseRoute}
          wheelEnabled={wheelOn}
          blackjackEnabled={blackjackOn}
          slotEnabled={slotOn}
        />
      </div>
    );
  }

  return (
    <div className="relative flex min-h-[100dvh] flex-col items-center px-4 pt-8 pb-28 sm:pt-12">
      <PinsBackground />

      <div className="relative z-10 my-auto flex w-full max-w-md flex-col">
        {/* Hero: the venue, in the spotlight */}
        <div className="flex flex-col items-center text-center">
          <BrandTile size="xl" animate />
          {/* Reserve space for the name to prevent CLS */}
          <h2
            className="pt-rise mt-5 min-h-[2rem] text-[26px] font-extrabold leading-tight text-foreground sm:text-3xl"
            style={rise(1)}
          >
            {client?.name || ' '}
          </h2>
          <h1
            className="pt-rise mt-1.5 text-[22px] font-bold leading-tight text-primary-ink sm:text-2xl"
            style={rise(2)}
          >
            Joue & gagne !
          </h1>
        </div>

        {/* Game selection - always visible */}
        <div className="mt-8 space-y-3">
          {games.map((g, i) => (
            <GameTile
              key={g.key}
              game={g.key}
              label={g.label}
              primary={i === 0}
              onClick={() => navigate(g.route)}
              className="pt-rise"
              style={rise(3 + i)}
            />
          ))}
        </div>

        {/* Rewards Marquee */}
        <div className="pt-rise mt-6" style={rise(3 + games.length)}>
          <RewardsMarquee showLabel={false} />
        </div>
      </div>

      <PlayerLegalFooter />
    </div>
  );
};
export default Index;
